/**
 * School Type Detection Utilities
 * 
 * Utilities to detect and categorize different types of schools in the EDC system:
 * - Standard NCES Schools: Official NCES schools with enrollment data
 * - Split Schools: Schools that have been divided into grade-level components  
 * - Non-NCES Schools: Custom schools without NCES IDs but with enrollment data
 * - Drop-a-Pin Locations: Geographic pins for market analysis with minimal data
 */

export const SCHOOL_TYPES = {
  STANDARD_NCES: 'standard_nces',
  SPLIT_SCHOOL: 'split_school', 
  NON_NCES: 'non_nces',
  DROP_A_PIN: 'drop_a_pin',
  UNKNOWN: 'unknown'
};

export const SCHOOL_TYPE_LABELS = {
  [SCHOOL_TYPES.STANDARD_NCES]: 'Standard NCES',
  [SCHOOL_TYPES.SPLIT_SCHOOL]: 'Split School',
  [SCHOOL_TYPES.NON_NCES]: 'Non-NCES School',
  [SCHOOL_TYPES.DROP_A_PIN]: 'Drop-a-Pin',
  [SCHOOL_TYPES.UNKNOWN]: 'Unknown'
};

export const SCHOOL_TYPE_COLORS = {
  [SCHOOL_TYPES.STANDARD_NCES]: 'primary',
  [SCHOOL_TYPES.SPLIT_SCHOOL]: 'secondary', 
  [SCHOOL_TYPES.NON_NCES]: 'success',
  [SCHOOL_TYPES.DROP_A_PIN]: 'warning',
  [SCHOOL_TYPES.UNKNOWN]: 'default'
};

export const SCHOOL_TYPE_DESCRIPTIONS = {
  [SCHOOL_TYPES.STANDARD_NCES]: 'Official NCES schools with full enrollment data',
  [SCHOOL_TYPES.SPLIT_SCHOOL]: 'Schools divided into grade-level components (ES/MS/HS)',
  [SCHOOL_TYPES.NON_NCES]: 'Custom schools without NCES IDs but with enrollment data',
  [SCHOOL_TYPES.DROP_A_PIN]: 'Geographic locations for market analysis (may include grades served)',
  [SCHOOL_TYPES.UNKNOWN]: 'School type could not be determined'
};

/**
 * Detect the school type based on school data
 * @param {Object} school - School object from the API
 * @param {Array} enrollmentData - Optional enrollment data array
 * @returns {string} School type constant
 */
export const detectSchoolType = (school, enrollmentData = []) => {
  if (!school) {
    return SCHOOL_TYPES.UNKNOWN;
  }

  // Check for split school - has NCES ID and split suffix
  const hasSplitSuffix = school.split_suffix || (school.full_ncessch && school.full_ncessch !== school.ncessch);
  if (school.ncessch && hasSplitSuffix) {
    return SCHOOL_TYPES.SPLIT_SCHOOL;
  }

  // Check for standard NCES school - has NCES ID and no split suffix
  if (school.ncessch && !hasSplitSuffix) {
    return SCHOOL_TYPES.STANDARD_NCES;
  }

  // No NCES ID - need to distinguish between Non-NCES school and Drop-a-Pin
  if (!school.ncessch) {
    // Use the enrollment data from the API response
    const hasEnrollmentData = !!(school.has_enrollment_data || school.total_enrollment > 0);

    // Non-NCES school: Has enrollment data (real school with students)
    // Drop-a-Pin: No NCES ID, no enrollment data (address/location only for market analysis)
    // Note: Drop-a-pins may have grades_offered for market analysis purposes, but no actual enrollment
    if (hasEnrollmentData) {
      return SCHOOL_TYPES.NON_NCES;
    }

    // Drop-a-Pin: No NCES ID, no enrollment data (may have grades for analysis)
    return SCHOOL_TYPES.DROP_A_PIN;
  }

  return SCHOOL_TYPES.UNKNOWN;
};

/**
 * Get school type information including label, color, and description
 * @param {string} schoolType - School type constant
 * @returns {Object} School type information
 */
export const getSchoolTypeInfo = (schoolType) => {
  return {
    type: schoolType,
    label: SCHOOL_TYPE_LABELS[schoolType] || 'Unknown',
    color: SCHOOL_TYPE_COLORS[schoolType] || 'default',
    description: SCHOOL_TYPE_DESCRIPTIONS[schoolType] || 'Unknown school type'
  };
};

/**
 * Check if a school type can be deleted
 * @param {string} schoolType - School type constant
 * @returns {boolean} Whether the school type can be deleted
 */
export const canDeleteSchoolType = (schoolType) => {
  // Only allow deletion of non-standard schools
  return [
    SCHOOL_TYPES.SPLIT_SCHOOL,
    SCHOOL_TYPES.NON_NCES,
    SCHOOL_TYPES.DROP_A_PIN
  ].includes(schoolType);
};

/**
 * Get deletion warning message for a school type
 * @param {string} schoolType - School type constant
 * @returns {string} Warning message
 */
export const getDeletionWarning = (schoolType) => {
  switch (schoolType) {
    case SCHOOL_TYPES.STANDARD_NCES:
      return 'Standard NCES schools cannot be deleted as they contain official educational data.';
    case SCHOOL_TYPES.SPLIT_SCHOOL:
      return 'This will delete the split school configuration and all associated data.';
    case SCHOOL_TYPES.NON_NCES:
      return 'This will delete the custom school and all associated enrollment and demographic data.';
    case SCHOOL_TYPES.DROP_A_PIN:
      return 'This will delete the location pin and any associated demographic analysis data.';
    default:
      return 'Deletion impact unknown for this school type.';
  }
};

/**
 * Filter schools by type
 * @param {Array} schools - Array of school objects
 * @param {string} schoolType - School type to filter by
 * @param {Array} enrollmentData - Optional enrollment data array
 * @returns {Array} Filtered schools array
 */
export const filterSchoolsByType = (schools, schoolType, enrollmentData = []) => {
  if (!schoolType || schoolType === 'all') {
    return schools;
  }

  return schools.filter(school => detectSchoolType(school, enrollmentData) === schoolType);
};

/**
 * Get counts of each school type
 * @param {Array} schools - Array of school objects
 * @param {Array} enrollmentData - Optional enrollment data array
 * @returns {Object} School type counts
 */
export const getSchoolTypeCounts = (schools, enrollmentData = []) => {
  const counts = {
    [SCHOOL_TYPES.STANDARD_NCES]: 0,
    [SCHOOL_TYPES.SPLIT_SCHOOL]: 0,
    [SCHOOL_TYPES.NON_NCES]: 0,
    [SCHOOL_TYPES.DROP_A_PIN]: 0,
    [SCHOOL_TYPES.UNKNOWN]: 0,
    total: schools.length
  };

  schools.forEach(school => {
    const type = detectSchoolType(school, enrollmentData);
    counts[type] = (counts[type] || 0) + 1;
  });

  return counts;
};

